import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader } from "@/components/ui/Loader";
import { enviarPesquisaSlug, fetchPesquisaSlug, type PesquisaPublicaPayload } from "@/services/pesquisaNps";
import { FormularioNps, type RespostaMap } from "./FormularioNps";
import { PesquisaEstado, PesquisaNpsLayout } from "./PesquisaNpsLayout";

function doneKey(slug: string): string {
  return `gg-nps-done:${slug}`;
}

export function PesquisaNpsSlugPage() {
  const { slug } = useParams<{ slug: string }>();
  const [meta, setMeta] = useState<PesquisaPublicaPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [jaEnviou, setJaEnviou] = useState(false);

  useEffect(() => {
    if (!slug) return;
    if (typeof window !== "undefined" && window.localStorage.getItem(doneKey(slug))) {
      setJaEnviou(true);
    }
    fetchPesquisaSlug(slug)
      .then(setMeta)
      .catch((e: unknown) => {
        const ax = e as { response?: { data?: PesquisaPublicaPayload } };
        setMeta(
          ax.response?.data ?? {
            success: false,
            estado: "token_invalido",
            error: "Esta pesquisa não está disponível no momento.",
          },
        );
      })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <PesquisaNpsLayout>
        <div className="flex justify-center py-16">
          <Loader label="Carregando pesquisa..." />
        </div>
      </PesquisaNpsLayout>
    );
  }

  const estado = meta?.estado;
  if (!meta || estado === "token_invalido" || estado === "identificar") {
    return <PesquisaEstado titulo="Indisponível" texto={meta?.error ?? "Esta pesquisa não está disponível no momento."} />;
  }
  if (estado === "indisponivel") {
    return <PesquisaEstado titulo="Indisponível" texto={meta.error ?? "Esta pesquisa não está disponível no momento."} />;
  }
  if (estado === "encerrada") {
    return <PesquisaEstado titulo="Pesquisa encerrada" texto={meta.error ?? "Esta pesquisa foi encerrada. Obrigado!"} />;
  }

  if (jaEnviou) {
    return (
      <PesquisaEstado
        titulo="Obrigado!"
        texto="Sua resposta já foi registrada neste aparelho."
      />
    );
  }

  if (estado === "pronta" && meta.pesquisa && meta.perguntas && slug) {
    const slugValue = slug;
    const perguntas = meta.perguntas;
    async function enviar(answers: RespostaMap) {
      setSubmitting(true);
      try {
        const itens = perguntas.map((p) => ({
          perguntaId: p.id,
          valorNumero: answers[p.id]?.valorNumero ?? null,
          valorTexto: answers[p.id]?.valorTexto ?? null,
        }));
        const res = await enviarPesquisaSlug(slugValue, itens);
        if (res.estado === "ok" || res.estado === "ja_respondeu") {
          try {
            window.localStorage.setItem(doneKey(slugValue), "1");
          } catch {
            /* ignore */
          }
          return;
        }
        throw Object.assign(new Error(res.error || "Falha ao enviar"), { response: { data: res } });
      } finally {
        setSubmitting(false);
      }
    }

    return (
      <PesquisaNpsLayout>
        <FormularioNps
          pesquisa={meta.pesquisa}
          perguntas={meta.perguntas}
          storageKey={`gg-nps-draft:${slugValue}`}
          onSubmit={enviar}
          submitting={submitting}
        />
      </PesquisaNpsLayout>
    );
  }

  return <PesquisaEstado titulo="Indisponível" texto="Esta pesquisa não está disponível no momento." />;
}
